import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Icon from "../../../components/Icon";
import Skeleton from "../../../components/Skeleton";
import { EmptyState, ErrorState } from "../../../components/State";
import { DonutChart, EmptyChartState } from "../../../components/Charts";
import {
  fetchAssetHealthSummary,
  fetchAssetHealthTrend,
} from "../services/CMDBService";

const labelForCategory = (value) => {
  const text = String(value || "asset").toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const pct = (value) => `${Number(value || 0).toFixed(1)}%`;

export default function AssetHealthAnalysisView() {
  const [summary, setSummary] = useState(null);
  const [device, setDevice] = useState("all");
  const [status, setStatus] = useState("all");
  const [range, setRange] = useState("24h");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trendSnapshots, setTrendSnapshots] = useState([]);
  const [trendLoading, setTrendLoading] = useState(true);
  const [trendError, setTrendError] = useState(null);
  const trendRequestIdRef = useRef(0);

  const load = useCallback(
    async (selectedRange = range) => {
      setLoading(true);
      setError(null);

      try {
        setSummary(await fetchAssetHealthSummary(selectedRange));
      } catch {
        setError("Could not load asset health analysis.");
      } finally {
        setLoading(false);
      }
    },
    [range],
  );

  useEffect(() => {
    load(range);
  }, [load, range]);

  const loadTrend = useCallback(async (selectedRange) => {
    const requestId = ++trendRequestIdRef.current;
    setTrendLoading(true);
    setTrendError(null);

    try {
      const response = await fetchAssetHealthTrend(selectedRange);
      if (requestId !== trendRequestIdRef.current) return;
      setTrendSnapshots(
        Array.isArray(response?.snapshots) ? response.snapshots : [],
      );
    } catch (requestError) {
      if (requestId !== trendRequestIdRef.current) return;
      setTrendSnapshots([]);
      setTrendError(
        requestError?.message || "Could not load infrastructure health trend.",
      );
    } finally {
      if (requestId === trendRequestIdRef.current) {
        setTrendLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadTrend(range);
  }, [loadTrend, range]);

  const chartSnapshots = useMemo(
    () =>
      trendSnapshots
        .filter((snapshot) => {
          if (
            !snapshot?.captured_at ||
            !Number.isFinite(Date.parse(snapshot.captured_at))
          ) {
            return false;
          }
          if (
            snapshot.overall_health_percent === null ||
            snapshot.overall_health_percent === undefined
          ) {
            return false;
          }
          return Number.isFinite(Number(snapshot.overall_health_percent));
        })
        .map((snapshot) => ({
          ...snapshot,
          overall_health_percent: Number(snapshot.overall_health_percent),
        }))
        .sort(
          (left, right) =>
            new Date(left.captured_at).getTime() -
            new Date(right.captured_at).getTime(),
        ),
    [trendSnapshots],
  );

  const allCategories = useMemo(
    () => summary?.categories || [],
    [summary],
  );
  const categories = useMemo(
    () =>
      allCategories.filter(
        (item) => device === "all" || item.asset_category === device,
      ),
    [allCategories, device],
  );
  const visibleCategories = useMemo(
    () =>
      categories.map((item) => {
        if (status === "all") return item;

        const activeCount =
          status === "active" ? Number(item.active_count || 0) : 0;
        const inactiveCount =
          status === "inactive" ? Number(item.inactive_count || 0) : 0;

        return {
          ...item,
          asset_count: activeCount + inactiveCount,
          active_count: activeCount,
          inactive_count: inactiveCount,
        };
      }),
    [categories, status],
  );
  const totals = useMemo(() => {
    if (device === "all" && status === "all") return summary || {};

    const totalAssets = categories.reduce(
      (count, item) => count + Number(item.asset_count || 0),
      0,
    );
    const activeAssets =
      status === "inactive"
        ? 0
        : categories.reduce(
            (count, item) => count + Number(item.active_count || 0),
            0,
          );
    const inactiveAssets =
      status === "active"
        ? 0
        : categories.reduce(
            (count, item) => count + Number(item.inactive_count || 0),
            0,
          );

    return {
      total_assets: totalAssets,
      active_assets: activeAssets,
      inactive_assets: inactiveAssets,
      weighted_health: visibleCategories.reduce(
        (totalHealth, item) =>
          totalHealth +
          Number(item.average_health_percent || 0) *
            Number(item.asset_count || 0),
        0,
      ),
    };
  }, [summary, categories, visibleCategories, device, status]);

  const total = Number(totals.total_assets || 0);
  const active = Number(totals.active_assets || 0);
  const inactive = Number(
    totals.inactive_assets ?? Math.max(total - active, 0),
  );
  const overall =
    device === "all" && status === "all"
      ? Number(totals.overall_health_percent || 0)
      : total
        ? totals.weighted_health / total
        : 0;
  const donut = [
    { name: "Active assets", value: active, color: "#22C55E" },
    { name: "Inactive assets", value: inactive, color: "#EF4444" },
  ];

  if (error) {
    return (
      <div className="asset-health-analysis">
        <ErrorState message={error} onRetry={() => load(range)} />
      </div>
    );
  }
  if (loading) {
    return (
      <div className="asset-health-analysis">
        <Skeleton height={42} />
        <Skeleton height={420} style={{ marginTop: 16 }} />
      </div>
    );
  }
  if (!summary) {
    return (
      <div className="asset-health-analysis">
        <EmptyState
          icon="activity"
          title="No asset health data"
          subtitle="No asset health summary is available yet."
        />
      </div>
    );
  }

  return (
    <div className="asset-health-analysis">
      <header className="asset-health-analysis__header">
        <div>
          <h1>Asset Health Analysis</h1>
          <p>Consolidated infrastructure health across Servers and Routers</p>
        </div>
        <span>
          Last updated:{" "}
          {summary.calculated_at
            ? new Date(summary.calculated_at).toLocaleString()
            : "—"}
        </span>
      </header>

      <div className="asset-health-analysis__filters">
        <select
          value={device}
          onChange={(event) => setDevice(event.target.value)}
        >
          <option value="all">All Devices</option>
          {allCategories.map((item) => (
            <option key={item.asset_category} value={item.asset_category}>
              {labelForCategory(item.asset_category)}
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select value={range} onChange={(event) => setRange(event.target.value)}>
          <option value="24h">24 Hours</option>
          <option value="7d">7 Days</option>
          <option value="30d">30 Days</option>
        </select>
        <button
          type="button"
          onClick={() => {
            load(range);
            loadTrend(range);
          }}
        >
          ↻&nbsp; Refresh
        </button>
      </div>

      <div className="asset-health-analysis__grid">
        <section className="asset-health-analysis__card asset-health-analysis__card--overall">
          <h2>Overall Infrastructure Health</h2>
          <p>Aggregated health across the complete infrastructure population</p>
          <div className="asset-health-analysis__overall-content">
            <div className="asset-health-analysis__donut">
              <DonutChart
                data={donut}
                innerRadius={86}
                outerRadius={126}
                height={330}
                paddingAngle={1}
                centerLabel={{
                  primary: pct(overall),
                  primarySize: 44,
                  secondary:
                    overall !== null && overall !== undefined
                      ? `● ${overall >= 90 ? "Healthy" : overall >= 50 ? "Degraded" : "Critical"}`
                      : undefined,
                  secondaryColor:
                    overall >= 90 ? "#16A34A" : overall >= 50 ? "#D97706" : "#DC2626",
                  sub:
                    summary.health_change_percent !== null &&
                    summary.health_change_percent !== undefined
                      ? `${summary.health_change_percent >= 0 ? "↑" : "↓"} ${Math.abs(summary.health_change_percent).toFixed(1)}% vs last ${
                          range === "24h"
                            ? "24 hours"
                            : range === "30d"
                              ? "30 days"
                              : "7 days"
                        }`
                      : undefined,
                }}
                tooltipFormatter={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  return (
                    <div className="asset-health-tooltip">
                      <strong>{payload[0].payload.name}</strong>
                      <span>{payload[0].value.toLocaleString()} assets</span>
                    </div>
                  );
                }}
              />
            </div>
            <div className="asset-health-analysis__stat-list">
              <div>
                <Icon name="layers" size={24} color="#1674E8" />
                <section>
                  <strong>{total.toLocaleString()}</strong>
                  <span>Total assets</span>
                </section>
              </div>
              <div>
                <Icon name="check" size={24} color="#16A34A" />
                <section>
                  <strong>{active.toLocaleString()}</strong>
                  <span>
                    Active assets · {pct(total ? (active * 100) / total : 0)}
                  </span>
                </section>
              </div>
              <div>
                <Icon name="x" size={24} color="#EF4444" />
                <section>
                  <strong>{inactive.toLocaleString()}</strong>
                  <span>
                    Inactive assets ·{" "}
                    {pct(total ? (inactive * 100) / total : 0)}
                  </span>
                </section>
              </div>
            </div>
          </div>
        </section>

        <section className="asset-health-analysis__card">
          <div className="asset-health-analysis__card-heading">
            <div>
              <h2>Infrastructure Health Trend</h2>
              <p>Collective health movement over time</p>
            </div>
            <div className="asset-health-analysis__range">
              {["24h", "7d", "30d"].map((value) => (
                <button
                  key={value}
                  className={range === value ? "active" : ""}
                  onClick={() => setRange(value)}
                >
                  {value}
                </button>
              ))}
            </div>
          </div>
          {trendLoading ? (
            <Skeleton height={270} style={{ marginTop: 16 }} />
          ) : trendError ? (
            <div className="asset-health-analysis__trend-empty">
              <ErrorState
                message={trendError}
                onRetry={() => loadTrend(range)}
              />
            </div>
          ) : chartSnapshots.length === 0 ? (
            <div className="asset-health-analysis__trend-empty">
              <EmptyChartState message="Historical trend data will appear after health snapshots are collected." />
            </div>
          ) : (
            <div style={{ width: "100%", height: 270, marginTop: 16 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartSnapshots}
                  margin={{ top: 8, right: 18, bottom: 8, left: 2 }}
                >
                  <CartesianGrid vertical={false} stroke="#F1F5F9" />
                  <XAxis
                    dataKey="captured_at"
                    tick={{ fontSize: 10, fill: "#94A3B8" }}
                    minTickGap={24}
                    tickFormatter={(value) => {
                      const capturedAt = new Date(value);
                      return range === "24h"
                        ? capturedAt.toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : capturedAt.toLocaleDateString([], {
                            month: "short",
                            day: "numeric",
                          });
                    }}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 10, fill: "#94A3B8" }}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <Tooltip
                    labelFormatter={(value) => new Date(value).toLocaleString()}
                    formatter={(value) => [
                      `${Number(value).toFixed(1)}%`,
                      "Overall health",
                    ]}
                    contentStyle={{ fontSize: 11, borderRadius: 8 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="overall_health_percent"
                    name="Overall health"
                    stroke="#1674E8"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                    connectNulls={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>
      </div>

      <section className="asset-health-analysis__card asset-health-analysis__availability">
        <h2>Availability</h2>
        <p>Active versus inactive population by device type</p>
        {visibleCategories.map((item) => {
          const count = Number(item.asset_count || 0);
          const activePercent = count
            ? (Number(item.active_count || 0) * 100) / count
            : 0;

          return (
            <div
              className="asset-health-analysis__availability-row"
              key={item.asset_category}
            >
              <div>
                <strong>{labelForCategory(item.asset_category)}s</strong>
                <span>
                  {pct(activePercent)} active · {pct(100 - activePercent)}
                  {" "}inactive
                </span>
              </div>
              <div className="asset-health-analysis__bar">
                <i style={{ width: `${activePercent}%` }} />
                <b style={{ width: `${100 - activePercent}%` }} />
              </div>
            </div>
          );
        })}
        <div className="asset-health-analysis__legend">
          <span>
            <i /> Active
          </span>
          <span>
            <i /> Inactive
          </span>
        </div>
      </section>
    </div>
  );
}
